/** Locale-aware formatting; money stays a decimal string until it is shown. */

/**
 * No-break space (U+00A0) between a number and its unit: with a plain space a
 * narrow column (the alerts tab on an iPad) broke «97 %» after the number and
 * left «%)» alone on the next line.
 */
export const NBSP = " ";

/** "97 %", "20 ГБ": a value glued to its unit. */
export function withUnit(value: string | number, unit: string): string {
  return `${value}${NBSP}${unit}`;
}

/** A percentage in the reader's locale, `digits` decimals at most. */
export function percent(value: number, locale?: string, digits = 0): string {
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);
  return withUnit(number, "%");
}

export function money(amount: string, currency: string, locale: string, digits = 2): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(amount));
}

export function dateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "5 minutes ago", "in 2 days"; under a minute reads as "now". */
export function relativeTime(iso: string, locale: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, "second");
}

const TB = 1024 ** 4;
export function terabytes(bytes: number, locale: string): string {
  return withUnit(
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / TB),
    "TB",
  );
}

const SIZE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

/** A data volume in the largest binary unit that keeps it ≥ 1 (traffic used, disk sizes). */
export function dataSize(bytes: number, locale: string): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);
  return withUnit(number, SIZE_UNITS[unit] ?? "B");
}
